import type {
  ContractTemplate,
  ContractTemplateCategory,
  ContractTemplateField,
} from "@/types/contractIntelligence.types";

export const TEMPLATE_CATEGORIES: ContractTemplateCategory[] = [
  { id: "all", label: "All", count: 24 },
  { id: "endorsement", label: "Endorsement", count: 6 },
  { id: "recording", label: "Music", count: 5 },
  { id: "licensing", label: "Licensing & IP", count: 4 },
  { id: "representation", label: "Representation", count: 3 },
  { id: "employment", label: "Employment", count: 3 },
  { id: "nda", label: "NDA / Compliance", count: 3 },
];

export const CONTRACT_TEMPLATES: ContractTemplate[] = [
  {
    id: "t-endorsement",
    category: "endorsement",
    name: "Athlete Endorsement Agreement",
    subtitle: "Standard 2-yr brand endorsement w/ exclusivity",
    color: "#22c55e",
    icon: "shield",
    pages: 14,
    use: "Most-used",
    examples: ["Nike — LeBron Royalty Refresh", "Wilson — Serena 25-yr extension"],
    sections: ["Parties", "Term", "Compensation", "Deliverables", "Exclusivity", "IP & Likeness", "Termination"],
  },
  {
    id: "t-recording",
    category: "recording",
    name: "Recording Artist Agreement",
    subtitle: "Album cycle, royalty splits, advance recoupment",
    color: "#8b5cf6",
    icon: "music",
    pages: 22,
    examples: ["Beyoncé — Renaissance II side letter"],
    sections: [
      "Parties",
      "Recording Commitment",
      "Advance & Royalties",
      "Master Rights",
      "Distribution",
      "Term & Options",
    ],
  },
  {
    id: "t-licensing",
    category: "licensing",
    name: "IP Licensing Agreement",
    subtitle: "License image / likeness / master to a brand or platform",
    color: "#ec4899",
    icon: "link",
    pages: 11,
    examples: ["Mamba Estate — Adidas image rights"],
    sections: ["Parties", "Licensed IP", "Territory", "Royalties", "Quality Control", "Term"],
  },
  {
    id: "t-rep",
    category: "representation",
    name: "Representation Agreement",
    subtitle: "Equal IQ as exclusive rep; commission tiers; carve-outs",
    color: "#4d6af5",
    icon: "briefcase",
    pages: 9,
    use: "Most-used",
    examples: ["Olivia Dunne onboarding", "Travis Scott renewal"],
    sections: ["Parties", "Scope of Services", "Commission", "Term", "Termination", "Carve-outs"],
  },
  {
    id: "t-nda",
    category: "nda",
    name: "Mutual NDA",
    subtitle: "Standard 3-year mutual confidentiality",
    color: "#f59e0b",
    icon: "lock",
    pages: 4,
    examples: ["Pre-deal Nike conversation", "Pitch deck shares"],
    sections: ["Parties", "Confidential Information", "Permitted Use", "Term", "Remedies"],
  },
  {
    id: "t-tour",
    category: "recording",
    name: "Tour & Live Performance",
    subtitle: "Multi-leg tour rider, settlement, and force majeure",
    color: "#22d3ee",
    icon: "mic",
    pages: 18,
    examples: ["Live Nation — Renaissance II"],
    sections: ["Parties", "Performance Schedule", "Rider", "Compensation", "Cancellation", "Liability"],
  },
  {
    id: "t-creator",
    category: "endorsement",
    name: "Creator Content Deal",
    subtitle: "Single-campaign creator deal w/ usage rights",
    color: "#ec4899",
    icon: "video",
    pages: 7,
    examples: ["Olivia Dunne — Vuori capsule"],
    sections: ["Deliverables", "Usage Rights", "Whitelisting", "Compensation", "Approvals"],
  },
  {
    id: "t-employment",
    category: "employment",
    name: "Senior Hire — Equal IQ",
    subtitle: "Manager / VP-level role at the agency",
    color: "#6c708a",
    icon: "user",
    pages: 8,
    examples: ["Director, Talent Strategy"],
    sections: ["Role", "Compensation", "Equity", "Confidentiality", "Non-compete", "Term"],
  },
];

export const TEMPLATE_FLOWS: Record<string, ContractTemplateField[]> = {
  "t-endorsement": [
    {
      id: "client",
      question: "Who's the talent on this deal?",
      kind: "select",
      options: [
        "LeBron James",
        "Beyoncé",
        "Travis Scott",
        "Serena Williams",
        "Kobe — Mamba Estate",
        "Other (type below)",
      ],
      hint: "Pick from your roster or type a new name.",
    },
    {
      id: "brand",
      question: "Who is the brand or counterparty?",
      kind: "text",
      placeholder: "e.g. Nike, McDonald's, Wilson Sporting Goods",
    },
    {
      id: "term",
      question: "What's the term length?",
      kind: "chips",
      options: ["1 year", "2 years", "3 years", "5 years", "Custom"],
    },
    {
      id: "fee",
      question: "Annual guarantee?",
      kind: "money",
      placeholder: "5,000,000",
      suggest: "Industry median for top-25 NBA talent: $4.2M – $7.5M / yr",
    },
    {
      id: "royalty",
      question: "Royalty rate on net sales of co-branded product?",
      kind: "percent",
      placeholder: "7",
      suggest: "Standard range 5–10% of net wholesale.",
    },
    {
      id: "exclusive",
      question: "Exclusivity scope?",
      kind: "chips",
      options: ["Category only", "Category + adjacent", "Whole industry", "None"],
      hint: "Affects which other deals talent can sign during term.",
    },
    {
      id: "territory",
      question: "Territory?",
      kind: "chips",
      options: ["North America", "Worldwide", "Worldwide ex-China", "Custom"],
    },
    {
      id: "morals",
      question: "Morals clause: who can invoke?",
      kind: "chips",
      options: ["Brand only", "Mutual", "None"],
      hint: "Mutual is preferred — protects the talent too.",
    },
    {
      id: "notes",
      question: "Anything else Ari should know? (optional)",
      kind: "longtext",
      placeholder: "e.g. needs a tour-blackout window in Q3 2026",
    },
  ],
  "t-recording": [
    {
      id: "artist",
      question: "Who is the recording artist?",
      kind: "select",
      options: ["Beyoncé", "Travis Scott", "New artist (type below)", "Other"],
    },
    {
      id: "label",
      question: "Label or counterparty?",
      kind: "text",
      placeholder: "e.g. Columbia, Republic, indie",
    },
    {
      id: "albums",
      question: "How many albums in this commitment?",
      kind: "chips",
      options: ["1 album", "2 albums", "3 albums", "4+ albums"],
    },
    {
      id: "advance",
      question: "Advance per album?",
      kind: "money",
      placeholder: "2,000,000",
    },
    {
      id: "rate",
      question: "Artist royalty rate (after recoupment)?",
      kind: "percent",
      placeholder: "18",
      suggest: "Standard major-label range: 14–22% (top-tier)",
    },
    {
      id: "masters",
      question: "Master ownership?",
      kind: "chips",
      options: ["Artist owns", "Label owns", "Reverts after 7 years", "Reverts after 15 years"],
    },
    {
      id: "options",
      question: "How many label option periods?",
      kind: "chips",
      options: ["No options", "1 option", "2 options", "3+ options"],
    },
  ],
  "t-rep": [
    { id: "client", question: "Who is the new client?", kind: "text", placeholder: "Full legal name" },
    {
      id: "scope",
      question: "What scope of services?",
      kind: "chips-multi",
      options: [
        "Endorsements",
        "Recording",
        "Touring",
        "Brand Partnerships",
        "Investments",
        "Legal",
        "Tax",
        "EA/Operations",
      ],
    },
    {
      id: "commission",
      question: "Commission rate?",
      kind: "percent",
      placeholder: "15",
      suggest: "Equal IQ standard: 15% on commercial deals, 10% on touring.",
    },
    {
      id: "tier",
      question: "Tiered or flat commission?",
      kind: "chips",
      options: ["Flat", "Tiered (drops above $X)", "Custom"],
    },
    {
      id: "term",
      question: "Initial term?",
      kind: "chips",
      options: ["1 year", "2 years", "3 years", "Indefinite w/ 90-day notice"],
    },
    {
      id: "carveouts",
      question: "Any carve-outs (deals you don't commission)?",
      kind: "longtext",
      placeholder: "e.g. existing Nike deal pre-dating this rep agreement",
    },
  ],
  "t-nda": [
    { id: "parties", question: "Other party name?", kind: "text", placeholder: "Counterparty legal entity" },
    {
      id: "purpose",
      question: "Purpose of disclosure?",
      kind: "longtext",
      placeholder: "e.g. exploring a potential endorsement partnership",
    },
    {
      id: "term",
      question: "Confidentiality period?",
      kind: "chips",
      options: ["1 year", "2 years", "3 years", "5 years"],
    },
    {
      id: "mutual",
      question: "Mutual or one-way?",
      kind: "chips",
      options: ["Mutual (recommended)", "One-way (we receive)", "One-way (we disclose)"],
    },
  ],
  _default: [
    { id: "parties", question: "Who are the parties?", kind: "text", placeholder: "Talent / counterparty" },
    {
      id: "term",
      question: "Term length?",
      kind: "chips",
      options: ["1 year", "2 years", "3 years", "5 years"],
    },
    { id: "value", question: "Total deal value?", kind: "money", placeholder: "1,000,000" },
    {
      id: "notes",
      question: "Anything else to capture?",
      kind: "longtext",
      placeholder: "Special clauses, dates, or context",
    },
  ],
};

export function getTemplateFlow(templateId: string): ContractTemplateField[] {
  return TEMPLATE_FLOWS[templateId] ?? TEMPLATE_FLOWS._default;
}
